import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';

import { BdReviewLog, BdReviewResolution } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import {
  BD_OUTCOME_LABELS,
  BD_RESOLUTION_LABELS,
  BD_SIGNAL_TYPE_LABELS,
  BD_TIER_LABELS,
} from '../constants';
import BdUserLink from './BdUserLink';

interface BdReviewRowProps {
  item: BdReviewLog;
  onResolve: (id: number, resolution: BdReviewResolution, notes?: string) => Promise<void>;
}

const TIER_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  CONFIRMED: 'error',
  MEDIUM: 'warning',
  LOW: 'default',
};

function BdReviewRow({ item, onResolve }: BdReviewRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<BdReviewResolution | null>(null);
  const [notes, setNotes] = useState('');

  const distinctSignals = Array.from(
    new Set(item.matchedSignals.map((signal) => signal.signalType)),
  );
  const opacity = item.reviewed ? 0.7 : 1;

  const handleConfirm = async () => {
    if (!confirmDialog) return;

    await onResolve(item.id, confirmDialog, notes || undefined);
    setConfirmDialog(null);
    setNotes('');
  };

  return (
    <>
      <TableRow sx={{ opacity }}>
        <TableCell sx={{ width: 40 }}>
          <Tooltip title={expanded ? 'Hide details' : 'Show details'}>
            <IconButton
              size='small'
              aria-label={expanded ? 'Hide details' : 'Show details'}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            size='small'
            label={`${BD_TIER_LABELS[item.tier] ?? item.tier} (${item.score})`}
            color={TIER_COLOR[item.tier] ?? 'default'}
          />
        </TableCell>
        <TableCell>
          {item.bookingId ? (
            <a href={`/bookings/lookup?id=${item.bookingId}`} target='_blank' rel='noreferrer'>
              {item.bookingId}
            </a>
          ) : (
            <em>—</em>
          )}
        </TableCell>
        <TableCell>
          <BdUserLink id={item.payorUserId} summary={item.payorSummary} />
        </TableCell>
        <TableCell>
          <BdUserLink id={item.payeeUserId} summary={item.payeeSummary} />
        </TableCell>
        <TableCell>
          {item.payorCompanyName ? (
            <Typography variant='body2'>{item.payorCompanyName}</Typography>
          ) : (
            <Typography variant='body2' sx={{ color: colors.gray[500] }}>—</Typography>
          )}
        </TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5} flexWrap='wrap'>
            {distinctSignals.map((sig) => (
              <Chip
                key={sig}
                size='small'
                label={BD_SIGNAL_TYPE_LABELS[sig] ?? sig}
                variant='outlined'
              />
            ))}
          </Stack>
        </TableCell>
        <TableCell>{BD_OUTCOME_LABELS[item.outcome] ?? item.outcome}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {new Date(item.created).toLocaleString()}
        </TableCell>
        <TableCell sx={{ minWidth: 240 }}>
          {item.reviewed ? (
            <Typography variant='body2' color='text.secondary'>
              {item.reviewResolution
                ? BD_RESOLUTION_LABELS[item.reviewResolution] ?? item.reviewResolution
                : 'Resolved'}
            </Typography>
          ) : (
            <Stack direction='row' spacing={1}>
              <Button
                size='small'
                variant='contained'
                color='error'
                onClick={() => setConfirmDialog('CONFIRMED_FRAUD')}
              >
                Fraud
              </Button>
              <Button
                size='small'
                variant='outlined'
                color='success'
                onClick={() => setConfirmDialog('LEGITIMATE')}
              >
                Legitimate
              </Button>
            </Stack>
          )}
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={10} sx={{ background: '#fafafa' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant='subtitle2' gutterBottom>
                Matched Signals
              </Typography>
              {item.matchedSignals.length === 0 ? (
                <Typography variant='body2'>None</Typography>
              ) : (
                item.matchedSignals.map((sig, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Chip
                      size='small'
                      label={BD_SIGNAL_TYPE_LABELS[sig.signalType] ?? sig.signalType}
                      sx={{ mr: 1 }}
                    />
                    <Typography variant='body2' component='span'>
                      {sig.signalValue} (weight {sig.weight})
                    </Typography>
                  </Box>
                ))
              )}
              {item.reviewNotes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant='subtitle2'>Reviewer Notes</Typography>
                  <Typography variant='body2'>{item.reviewNotes}</Typography>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>
          Resolve as {confirmDialog && BD_RESOLUTION_LABELS[confirmDialog]}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            {confirmDialog === 'LEGITIMATE'
              ? 'This will allowlist the (payor, payee) pair so future bookings between them are '
                + 'allowed without re-evaluation.'
              : 'This will mark the review as resolved.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Notes (optional)'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button onClick={handleConfirm} variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BdReviewRow;
